import { useIntl } from '@leagueplatform/locales';
import { type ConfigError, ConfigErrorType } from '@web-config-app/core';
import { useConfigUserFeedback } from '../../state/config-user-feedback-state/config-user-feedback-state';

/**
 * Constructs the primary error message from a ConfigError object.
 * Invoked when there is a server error or the first error detail is empty/whitespace.
 * @param configError - The ConfigError object with structure: { status, name, message, errors: [] }
 * @param fallbackErrorMessage - Localized fallback when no errors array or first error detail is provided by the service
 * @returns A formatted error message string
 *   - If no configError: returns empty string
 *   - If first error has non-empty detail: returns that detail
 *   - Otherwise: returns the provided 'Server did not provide an error message'
 */
export const constructErrorMessage = (
  configError: ConfigError | null,
  fallbackErrorMessage: string,
) => {
  if (!configError) return '';
  if (
    configError?.errors?.[0]?.detail &&
    configError?.errors?.[0]?.detail.trim() !== ''
  ) {
    return configError.errors?.[0]?.detail;
  }
  return fallbackErrorMessage;
};

const isErrorDetailEmpty = (detail?: string): boolean =>
  !detail || detail.trim() === '';

/**
 * useConfigErrorBanner assembles data for the error banner based on the current error state.
 *
 * @param errorContextLabel - Optional display name for the error source used in
 *   the banner title. Callers with domain/entity
 *   context can pass the entity name; admin pages can pass their own operation label or omit it.
 * @returns {Object}
 *   - showErrorBanner (boolean): Whether the error banner should be visible
 *   - hideErrorBanner (function): Callback to hide the error banner
 *   - errorBannerTitle (string): Localized title for the banner based on error type (e.g., "Server Error", "Validation Error" - fallback to 'ERROR')
 *   - errorBannerContents (Object): Either an object with a primary message OR a list of detailed errors
 *     - When ServerError or the first detail is empty/whitespace: { errorMessage: string, secondaryErrorMessage?: string }
 *       - errorMessage: primary message (detail or localized fallback when detail is empty)
 *       - secondaryErrorMessage: optional secondary message sourced from the error 'message' field
 *     - Otherwise: { errorList: ConfigError[] } - an array of detailed error objects
 *   - showFormValidation (boolean): Whether form validation should be displayed
 */
export const useConfigErrorBanner = (errorContextLabel?: string) => {
  const { formatMessage } = useIntl();

  const {
    errorType,
    configError,
    showFormValidation,
    filteredErrorKeywords,
    showErrorBanner,
    hideErrorBanner,
  } = useConfigUserFeedback((state) => ({
    showErrorBanner: state.showErrorBanner && Boolean(state.configError),
    errorType: state.configError?.type,
    configError: state.configError,
    showFormValidation: state.showFormValidation,
    filteredErrorKeywords: state.filteredErrorKeywords,
    hideErrorBanner: () => state.setShowErrorBanner(false),
  }));

  const errorArrayAmount = configError?.errors?.length;

  const bannerTitle: Record<ConfigErrorType, string> = {
    [ConfigErrorType.ServerError]: 'SERVER_ERROR',
    [ConfigErrorType.DomainEntityError]: 'DOMAIN_ENTITY_CONFIGURATION_ERROR',
    [ConfigErrorType.FormValidationError]: 'VALIDATION_CONFIGURATION_ERROR',
  };

  // Get the banner title based on errorType, defaulting to 'ERROR'
  const errorBannerTitle = formatMessage(
    {
      id: errorType ? bannerTitle[errorType] : 'ERROR',
    },
    { count: errorArrayAmount, entityConfigName: errorContextLabel },
  );

  const serverFallbackErrorMessage = formatMessage({
    id: 'SERVER_DID_NOT_PROVIDE_AN_ERROR_MESSAGE',
  });
  /*  If a server error or empty error details, return an object with a primary
   *  errorMessage and built secondaryErrorMessage. Otherwise, return an array
   *  of error objects to be displayed as a list.
   */
  const errorBannerContents =
    errorType === ConfigErrorType.ServerError ||
    isErrorDetailEmpty(configError?.errors?.[0]?.detail)
      ? {
          errorMessage: constructErrorMessage(
            configError,
            serverFallbackErrorMessage,
          ),
          secondaryErrorMessage: configError?.message,
        }
      : { errorList: configError?.errors || [] };
  return {
    showErrorBanner,
    hideErrorBanner,
    errorBannerContents: showErrorBanner
      ? errorBannerContents
      : { errorList: [] },
    errorBannerTitle,
    showFormValidation,
    filteredErrorKeywords,
  };
};
